/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {AbstractService} from '@amblique/base/shared/services/abstract-service';

/**
 * ContentfulService - Contentful CMS Integration
 *
 * A streamlined service for working with Contentful content using pre-generated queries.
 *
 * Key Features:
 * - Type-safe: Leverages global Contentful types for IDE autocomplete
 * - Uses pre-generated GraphQL queries from `shared/graphql/queries/`
 * - Automatic locale from current site
 * - Preview mode support
 * - Comprehensive error handling
 * - Query result transformation utilities
 *
 * Type System:
 * - Uses global `Contentful` namespace from global.d.ts
 * - Specify concrete types (e.g., Contentful.Page, Contentful.Blog) based on your queries
 * - All Contentful types are auto-generated from your schema
 *
 * Usage:
 * ```javascript
 * import {print} from 'graphql';
 * import {getPageBySlug} from '@amblique/contentful/shared/graphql/queries';
 * import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
 *
 * const contentful = new ContentfulService({site});
 * const data = await contentful.query(print(getPageBySlug), {
 *   slug: 'home',
 *   locale: contentful.locale, // Automatically set from site
 *   preview: false
 * });
 * const page = data.pageCollection?.items?.[0] || null;
 * ```
 *
 * @extends AbstractService
 */
export class ContentfulService extends AbstractService {
  /**
   * @returns {string} Service identifier
   */
  static get name() {
    return 'ContentfulService';
  }

  /**
   * Service configuration profile
   * Uses Contentful GraphQL Content Delivery API (or Preview API)
   *
   * @returns {Object} Configuration object with baseUrl, options, and dynamic properties
   */
  static get profile() {
    return {
      baseUrl: 'https://graphql.contentful.com/content/v1/spaces/{site.preferences.contentful.spaceId}/environments/{site.preferences.contentful.environment}',
      locale: '{locale.id}',
      dynamicProperties: ['baseUrl', 'locale', 'options.headers.Authorization'],
      options: {
        headers: {
          'Authorization': 'Bearer {site.preferences.contentful.deliveryAccessToken}',
          'Content-Type': 'application/json',
        },
      },
    };
  }

  /**
   * Required template variables for service instantiation
   *
   * @returns {string[]} Array of required variable names
   */
  static get variables() {
    return ['locale', 'site'];
  }

  /**
   * Execute a GraphQL query
   *
   * Use pre-generated queries from `@amblique/contentful/shared/graphql/queries` for type safety.
   *
   * @example
   * import {print} from 'graphql';
   * import {getPageBySlug, getAllPages} from '@amblique/contentful/shared/graphql/queries';
   *
   * // Fetch by slug
   * const data = await contentful.query(print(getPageBySlug), {
   *   slug: 'home',
   *   locale: contentful.locale, // Automatically from site
   *   preview: false
   * });
   * const page = data.pageCollection?.items?.[0] || null;
   *
   * // Fetch collection
   * const data = await contentful.query(print(getAllPages), {
   *   locale: contentful.locale, // Automatically from site
   *   limit: 10,
   *   skip: 0,
   *   order: ['sys_publishedAt_DESC'],
   *   where: {},
   *   preview: false
   * });
   * const pages = data.pageCollection;
   *
   * @param {string} query - GraphQL query string (use `print()` to convert from AST)
   * @param {Object} [variables={}] - GraphQL query variables
   * @param {RequestInit} [options={}] - HTTP request options (headers, cache, etc.)
   * @returns {Promise<Object>} GraphQL response data
   */
  async query(query, variables = {}, options = {}) {
    return this.post('', {query, variables}, options);
  }

  /**
   * @override
   *  - Handle GraphQL-specific errors
   *
   * @protected
   * @param {Response} response
   * @returns {Promise<object | string>}
   */
  async parseResponse(response) {
    const {data, errors} = await super.parseResponse(response);

    if (errors) {
      const errorDetails = errors.map((e) => e.message).join('; ');
      const error = new Error(`Contentful GraphQL Error: ${errorDetails}`);

      error.name = 'ContentfulError';
      error.graphqlErrors = errors;
      error.status = response.status;

      throw error;
    }

    return data;
  }
}
